import React, { useState, useEffect, useCallback } from 'react'
import defaultMachinePhotoUrl from '../../assets/machine.png'
import machineVerificationCodeImage from '../../img/machine-verification-code.png'
import logoMyTest from '../../assets/logo.png'
import CustomConfirmBtn from '../customConfirmBtn/custom_confirm_btn'
import CustomModal from '../customModal/custom_modal';
import DispensingFailed from '../../assets/dispensingFailed.png'
import { HivTestService, VendingMachineService } from '../../api/v1/api_pb';
import { useClient } from '../../connect_api';
import { useNavigate, useParams } from 'react-router-dom';
import { DispenseStatus, VendingMachineInfo } from '../../api/v1/api_pb';
import { Helmet } from 'react-helmet';
import TextBtn from '../textButton/text_button';
import CustomPinInput from '../accessCode/custom_pin_input';

export default function MachineDetailPage() {
    const [failedModalOpen, setFailedModalOpen] = useState(false);
    const [codeErrorModalOpen, setCodeErrorModalOpen] = useState(false);
    const [isDispensing, setIsDispensing] = useState(false);
    const [machine, setMachine] = useState<VendingMachineInfo>();
    const [vendingMachineId, setVendingMachineId] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [verificationCode, setVerificationCode] = useState<string>('');
    const [isVerified, setIsVerified] = useState(false);
    const [verificationError, setVerificationError] = useState<string>('');
    const [matchedMachine, setMatchedMachine] = useState<VendingMachineInfo | null>(null);
    const [allVendingMachines, setAllVendingMachines] = useState<VendingMachineInfo[]>([]);
    const [isConfirmationChecked, setIsConfirmationChecked] = useState(false);
    const client = useClient(HivTestService);
    const vendingMachineClient = useClient(VendingMachineService);
    const { id: code } = useParams();
    const navigate = useNavigate();
    let startTime: number | null = null;
    // const timeoutInMs = 120000; // two minutes
    const timeoutInMs = 60000; // one minutes

    const findVendingMachine = useCallback(async () => {
        try {
            const response = await client.findVendingMachine({ machineCode: code });
            setMachine(response.vendingMachine);
            setVendingMachineId(response.vendingMachineId);
        } catch (error) {
            openCodeErrorModal();
        }
    }, [client, code]);

    const loadAllVendingMachines = useCallback(async () => {
        try {
            const response = await vendingMachineClient.listVendingMachines({});
            setAllVendingMachines(response.vendingMachines);
        } catch (error) {
            console.error('Failed to load vending machines:', error);
        }
    }, [vendingMachineClient]);

    const validateVerificationCode = useCallback((enteredCode: string) => {
        if (enteredCode.length !== 4) {
            return;
        }

        setVerificationError('');
        setMatchedMachine(null);

        // Check if the entered code matches the current machine's verification code
        if (enteredCode === machine?.verificationCode) {
            // Correct code for this machine
            setIsVerified(true);
            setVerificationError('');
            setMatchedMachine(null);
        } else {
            // Check if code matches any other machine
            const otherMachine = allVendingMachines.find(
                (m) => m.verificationCode === enteredCode && m.machineCode !== machine?.machineCode
            );

            if (otherMachine) {
                // Code matches a different machine
                setIsVerified(false);
                setMatchedMachine(otherMachine);
                setVerificationError('');
            } else {
                // Code doesn't match any machine
                setIsVerified(false);
                setMatchedMachine(null);
                setVerificationError("Sorry, that code doesn't look right. Please check that you have entered the code correctly and ensure that the machine location selected is the same as the machine you are standing in front of.");
            }
        }
    }, [machine, allVendingMachines]);

    useEffect(() => {
        const getLoginInfoFunction = async () => {
            await client.getLoginInfo({}).then((response) => {
                setIsButtonDisabled((response.tokenInfo?.remainingDispenseCount ?? 0) > 0);
            }).catch((error: any) => {
                let errorMessage: string = 'An unknown error occurred';
                const errorString: string = error.toString();
                const match = errorString.match(/\]\s*(.+)/);
                if (match && match[1]) {
                    errorMessage = match[1].trim();
                }
                setErrorMessage(errorMessage);
                openFailedModal();
                return;
            });
        };
        getLoginInfoFunction();
        findVendingMachine();
        loadAllVendingMachines();
    }, [client, code, navigate, findVendingMachine, loadAllVendingMachines]);

    // Auto-verify if machine has no verification code
    useEffect(() => {
        if (machine && (!machine.verificationCode || machine.verificationCode.trim() === '')) {
            setIsVerified(true);
        }
    }, [machine]);


    const [isPendingMachineInput, setIsPendingMachineInput] = useState(false);

    const dispenseTestKit = async () => {
        try {
            const response = await client.dispenseTestKit({ vendingMachineId: vendingMachineId });
            const pollingTimer = setInterval(async () => {
                if (startTime === null) {
                    startTime = Date.now();
                }

                // checkout time out
                if (Date.now() - startTime >= timeoutInMs) {
                    clearInterval(pollingTimer);
                    setErrorMessage('Checkout Timed Out');
                    setIsDispensing(false);
                    openFailedModal();
                    return;
                }
                try {
                    const stateResponse = await client.queryDispenseStatus({ dispenseId: response.dispenseId });
                    if (stateResponse.dispenseStatus === DispenseStatus.FAILED) {
                        clearInterval(pollingTimer);
                        setIsDispensing(false);
                        openFailedModal();
                    }

                    // customer has made selection on the vending machine.
                    if (stateResponse.dispenseStatus === DispenseStatus.DISPENSING) {
                        console.log('set is pending false');
                        setIsPendingMachineInput(false);
                    }

                    if (stateResponse.dispenseStatus === DispenseStatus.DISPENSED) {
                        clearInterval(pollingTimer);
                        setIsDispensing(false);
                        navigate('/thank-you');
                    }
                } catch (error) {
                    clearInterval(pollingTimer);
                    setErrorMessage('An unknown error occurred');
                    setIsDispensing(false);
                    openFailedModal();
                }
            }, 1000);
        } catch (error: any) {
            let errorMessage: string = 'An unknown error occurred';
            const errorString: string = error.toString();
            const match = errorString.match(/\]\s*(.+)/);
            if (match && match[1]) {
                errorMessage = match[1].trim();
            }
            setErrorMessage(errorMessage);
            setIsDispensing(false);
            openFailedModal();
        }
    };


    const handleVerificationCodeChange = (value: string) => {
        setVerificationCode(value);
        if (value.length === 4) {
            validateVerificationCode(value);
        } else {
            setVerificationError('');
            setIsVerified(false);
        }
    };

    const handleConfirmAction = () => {
        // Check if verification is required and not completed
        if (!isVerified) {
            alert('Please enter the 4-digit code displayed on the machine.');
            return;
        }

        // Check if confirmation checkbox is required and not checked
        if ((!machine?.verificationCode || machine.verificationCode.trim() === '') && !isConfirmationChecked) {
            alert('Please confirm that you are standing in front of the machine.');
            return;
        }

        if (code !== undefined) {
            setIsDispensing(true);

            // if it's dumb machine
            if (machine?.noVoti === true) {
                console.log('set is pending true');
                setIsPendingMachineInput(true);
            }

            dispenseTestKit();
        }
    };


    const openFailedModal = () => setFailedModalOpen(true);
    const openCodeErrorModal = () => setCodeErrorModalOpen(true);

    const handleSwapMachine = (machineCode: string) => {
        // Clear all verification states
        setVerificationCode('');
        setVerificationError('');
        setMatchedMachine(null);
        setIsVerified(false);
        // Navigate to the new machine, replacing current history entry
        navigate('/machine-detail/' + machineCode, { replace: true });
    };

    return (
        <div className="container justify-content-center align-items-center">
            <Helmet>
                <title>MachineDetail Page</title>
            </Helmet>
            {codeErrorModalOpen && <CustomModal title={'Error: Invalid Machine Code'} imageUrl={DispensingFailed} text={`The machine code you entered is incorrect`} isShowContactUs={false} cancelButtonTitle='Close' closeModal={() => {
                setCodeErrorModalOpen(false);
                navigate('/find-machine', {
                    state: true
                });
            }} />}
            {failedModalOpen && <CustomModal title={'Error: Dispensing Failed'} imageUrl={DispensingFailed} text={errorMessage !== '' ? errorMessage : `We couldn't dispense the product at the moment. Please try again later or `} isShowContactUs={errorMessage !== '' ? false : true} cancelButtonTitle='Close' closeModal={() => {
                setFailedModalOpen(false);
                navigate('/');
            }} />}
            <div className="py-1 px-3">
                {/* Header with back link and logo */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: '40px',
                    paddingBottom: '24px',
                    position: 'relative'
                }}>
                    <button
                        onClick={() => navigate('/find-machine')}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#400093',
                            fontFamily: 'Inter',
                            fontSize: '16px',
                            lineHeight: '1.5',
                            cursor: 'pointer',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}
                    >
                        &lt; Back
                    </button>
                    <img
                        src={logoMyTest}
                        alt="MyTest logo"
                        style={{
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            height: '42px',
                            width: '72px',
                            objectFit: 'contain'
                        }}
                    />
                </div>

                <h1 style={{
                    fontFamily: 'Roca',
                    fontSize: '32px',
                    lineHeight: '1.2',
                    color: '#400093',
                    marginBottom: '16px',
                    marginTop: '20px'
                }}>
                    Ready to dispense your self-test kit?
                </h1>

                <p style={{
                    fontFamily: 'Inter',
                    fontSize: '16px',
                    fontWeight: 400,
                    lineHeight: '1.5',
                    color: '#400093',
                    marginBottom: '32px'
                }}>
                    You must be standing in front of the machine you selected in order for the test kit to dispense.
                </p>

                {/* Purple card with machine info and image */}
                <div style={{
                    backgroundColor: '#30006e',
                    borderRadius: '16px',
                    padding: '16px',
                    display: 'flex',
                    gap: '12px',
                    marginBottom: '32px',
                    alignItems: 'flex-start'
                }}>
                    <div style={{ flex: 1 }}>
                        <p style={{
                            fontFamily: 'Inter',
                            fontSize: '14px',
                            color: 'white',
                            marginBottom: '8px',
                            lineHeight: '22px'
                        }}>
                            Vending machine location:
                        </p>
                        <h2 style={{
                            fontFamily: 'Roca',
                            fontSize: '24px',
                            color: 'white',
                            marginBottom: '8px',
                            lineHeight: 'normal'
                        }}>
                            {machine?.name}
                        </h2>
                        <p style={{
                            fontFamily: 'Inter',
                            fontSize: '16px',
                            color: 'white',
                            lineHeight: '22px'
                        }}>
                            {machine?.address}
                        </p>
                    </div>
                    <div style={{ position: 'relative', width: '116px', flexShrink: 0 }}>
                        <img
                            src={machine?.photoUrl ? machine?.photoUrl : defaultMachinePhotoUrl}
                            alt='Vending machine'
                            style={{
                                width: '100%',
                                height: 'auto',
                                borderRadius: '8px',
                                objectFit: 'cover'
                            }}
                        />
                        {machine?.photos && machine.photos.length > 0 && (
                            <button
                                onClick={() => navigate(`/machine-detail/${code}/photos`, {
                                    state: {
                                        photos: machine.photos,
                                        machineName: machine.name
                                    }
                                })}
                                style={{
                                    position: 'absolute',
                                    bottom: '8px',
                                    right: '8px',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    backgroundColor: '#B2F100',
                                    border: 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#9DD400';
                                    e.currentTarget.style.transform = 'scale(1.1)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#B2F100';
                                    e.currentTarget.style.transform = 'scale(1)';
                                }}
                                aria-label="View photos"
                            >
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M2.5 6.66667C2.5 5.74619 3.24619 5 4.16667 5H5.41667C5.69862 5 5.96305 4.86417 6.12731 4.63446L7.20602 3.19888C7.37028 2.96917 7.63471 2.83333 7.91667 2.83333H12.0833C12.3653 2.83333 12.6297 2.96917 12.794 3.19888L13.8727 4.63446C14.037 4.86417 14.3014 5 14.5833 5H15.8333C16.7538 5 17.5 5.74619 17.5 6.66667V15C17.5 15.9205 16.7538 16.6667 15.8333 16.6667H4.16667C3.24619 16.6667 2.5 15.9205 2.5 15V6.66667Z" stroke="#400093" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M10 13.3333C11.3807 13.3333 12.5 12.214 12.5 10.8333C12.5 9.45262 11.3807 8.33333 10 8.33333C8.61929 8.33333 7.5 9.45262 7.5 10.8333C7.5 12.214 8.61929 13.3333 10 13.3333Z" stroke="#400093" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        )}
                    </div>
                </div>

                {/* Verification Code Section - only show if machine has a verification code */}
                {machine?.verificationCode && machine.verificationCode.trim() !== '' && (
                    <>
                        {matchedMachine ? (
                            <div style={{ marginBottom: '20px' }}>
                                <p style={{
                                    fontFamily: 'Inter',
                                    fontSize: '16px',
                                    fontWeight: 400,
                                    lineHeight: '1.3',
                                    letterSpacing: '0.2px',
                                    color: '#cf2127',
                                    marginBottom: '16px'
                                }}>
                                    It looks like you entered a code from a different machine. Do you want to register for this machine instead:
                                </p>
                                <div
                                    onClick={() => handleSwapMachine(matchedMachine.machineCode)}
                                    style={{
                                        backgroundColor: '#30006e',
                                        borderRadius: '16px',
                                        padding: '16px',
                                        cursor: 'pointer',
                                        marginBottom: '16px'
                                    }}
                                >
                                    <div style={{
                                        display: 'flex',
                                        gap: '12px',
                                        marginBottom: '16px',
                                        alignItems: 'flex-start'
                                    }}>
                                        <div style={{ flex: 1 }}>
                                            <h2 style={{
                                                fontFamily: 'Roca',
                                                fontSize: '24px',
                                                color: 'white',
                                                marginBottom: '8px',
                                                lineHeight: 'normal'
                                            }}>
                                                {matchedMachine.name}
                                            </h2>
                                            <p style={{
                                                fontFamily: 'Inter',
                                                fontSize: '16px',
                                                color: 'white',
                                                lineHeight: '22px',
                                                margin: 0
                                            }}>
                                                {matchedMachine.address}
                                            </p>
                                        </div>
                                        <div style={{ position: 'relative', width: '116px', flexShrink: 0 }}>
                                            <img
                                                src={matchedMachine.photoUrl || defaultMachinePhotoUrl}
                                                alt='Matched vending machine'
                                                style={{
                                                    width: '100%',
                                                    height: 'auto',
                                                    borderRadius: '8px',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div style={{
                                        backgroundColor: '#b2f100',
                                        borderRadius: '50px',
                                        padding: '12px 30px',
                                        textAlign: 'center'
                                    }}>
                                        <p style={{
                                            fontFamily: 'Inter',
                                            fontSize: '18px',
                                            fontWeight: 600,
                                            color: '#066448',
                                            margin: 0,
                                            lineHeight: 'normal'
                                        }}>
                                            Swap machine
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : verificationError ? (
                            <>
                                <div className="error-box">
                                    <p>{verificationError}</p>
                                </div>
                                <button
                                    onClick={() => navigate('/find-machine')}
                                    style={{
                                        fontFamily: 'Inter',
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        color: '#400093',
                                        backgroundColor: 'transparent',
                                        border: '2px solid #400093',
                                        borderRadius: '50px',
                                        padding: '12px 30px',
                                        cursor: 'pointer',
                                        marginTop: '16px',
                                        marginBottom: '16px',
                                        width: '100%',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#400093';
                                        e.currentTarget.style.color = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.color = '#400093';
                                    }}
                                >
                                    Go back to vending machine list
                                </button>
                            </>
                        ) : null}

                        <div className="verification-section">
                            <p className="verification-label">
                                Enter the 4-digit code displayed on the front of the machine:
                            </p>
                            <CustomPinInput
                                key={code}
                                length={4}
                                onChange={handleVerificationCodeChange}
                                initialValue={verificationCode}
                                variant="large-purple"
                            />
                        </div>
                    </>
                )}

                {/* Confirmation checkbox - only show when no verification code is required */}
                {(!machine?.verificationCode || machine.verificationCode.trim() === '') && (
                    <div style={{ marginTop: '24px', marginBottom: '16px' }}>
                        <label style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '12px',
                            cursor: 'pointer',
                            fontFamily: 'Inter',
                            fontSize: '16px',
                            color: '#400093',
                            lineHeight: '1.5'
                        }}>
                            <input
                                type="checkbox"
                                checked={isConfirmationChecked}
                                onChange={(e) => setIsConfirmationChecked(e.target.checked)}
                                style={{
                                    width: '20px',
                                    height: '20px',
                                    marginTop: '2px',
                                    cursor: 'pointer',
                                    accentColor: '#400093'
                                }}
                            />
                            <span>
                                I am standing in front of this machine at {machine?.name || 'this location'}
                            </span>
                        </label>
                    </div>
                )}

                {/* Collect button */}
                <div style={{ marginTop: '24px', marginBottom: '24px' }}>
                    {isDispensing ? (
                        isPendingMachineInput ? (
                            <div className={`background-box mb-4 ${isDispensing ? 'dispensing' : ''}`}>
                                <div className="progress">Please select 11 on the vending machine</div>
                            </div>
                        ) : (
                            <div className={`background-box mb-4 ${isDispensing ? 'dispensing' : ''}`}>
                                <div className="progress">Dispensing..</div>
                            </div>
                        )
                    ) : (
                        <CustomConfirmBtn
                            disabled={!isButtonDisabled || !isVerified || ((!machine?.verificationCode || machine.verificationCode.trim() === '') && !isConfirmationChecked)}
                            confirmText={!isButtonDisabled ? 'Monthly quota exhausted' : `Collect kit now`}
                            confirmAction={!isButtonDisabled ? () => { } : handleConfirmAction}
                        />
                    )}
                </div>

                {/* Info Box - Where to find the code - only show if machine has verification code */}
                {machine?.verificationCode && machine.verificationCode.trim() !== '' && (
                    <div className="info-box">
                        <p style={{
                            fontFamily: 'Inter',
                            fontSize: '16px',
                            fontWeight: 400,
                            color: '#400093',
                            marginBottom: '16px',
                            lineHeight: '1.3',
                            letterSpacing: '0.2px'
                        }}>
                            Where to find the code:
                        </p>
                        <div className="info-content">
                            <img
                                src={machineVerificationCodeImage}
                                alt="Machine with code location"
                                className="info-image"
                            />
                        </div>
                        <div style={{
                            fontFamily: 'Inter',
                            fontSize: '16px',
                            color: '#400093',
                            lineHeight: '22px',
                            marginTop: '16px'
                        }}>
                            {machine?.locationName && (
                                <p style={{ marginBottom: '10px' }}>
                                    Machine location: {machine.locationName}
                                </p>
                            )}
                            {machine?.openingHours && (
                                <p>Machine hours: {machine.openingHours}</p>
                            )}
                        </div>
                        <div style={{
                            fontFamily: 'Inter',
                            fontSize: '16px',
                            color: '#400093',
                            lineHeight: '1.3',
                            letterSpacing: '0.2px',
                            marginTop: '16px'
                        }}>
                            <p style={{ marginBottom: '10px' }}>
                                Please ensure you have your mobile phone or another internet-connected device with you when you're in front of the vending machine.
                            </p>
                            <p>
                                You'll need it to enter the 4-digit code and dispense your self-test kit on this screen
                            </p>
                        </div>
                        <hr style={{
                            border: 'none',
                            borderTop: '1px solid #D9CDF4',
                            margin: '16px 0'
                        }} />
                        <p style={{
                            fontFamily: 'Inter',
                            fontSize: '16px',
                            color: '#400093',
                            lineHeight: 'normal',
                            marginTop: '16px'
                        }}>
                            <strong>Not ready to collect now or need to change dispensing location? </strong>
                            You can easily return to your dashboard later by clicking the link in your <strong>SMS or email.</strong>
                        </p>
                    </div>
                )}

                <div className={"py-4"}>
                    <TextBtn textButtonTitle='Go Back' textBtnFunction={() => {
                        navigate('/find-machine');
                    }} />
                </div>
            </div>
        </div>
    )
}
